import { useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { useScrollTriggerAutoRefresh } from '@/utils/useScrollTriggerAutoRefresh';

type UseParallaxOpts = {
  containerRef: React.RefObject<HTMLElement>;
  distancePx?: number; // distance in pixels (mirrors existing code)
  // Optional explicit start/end positions in pixels. When provided, these
  // take precedence over `distancePx` and allow negative values (e.g. -8 to 8).
  startPx?: number;
  endPx?: number;
  isEnabled?: boolean;
  start?: string;
  end?: string;
};

/**
 * Hook: Initializes GSAP parallax on elements with the `js-parallax` class
 * inside the provided container. This mirrors the implementation used
 * in the Hero component so it can be reused across components.
 */
export function useParallax({ containerRef, distancePx = 8, startPx, endPx, isEnabled = true, start, end }: UseParallaxOpts) {
  useGSAP(() => {
    if (!isEnabled) return;

    gsap.registerPlugin(ScrollTrigger);

    const parallaxContainer = containerRef?.current;
    if (!parallaxContainer) return;

    const ctx = gsap.context(() => {
      const items = Array.from(parallaxContainer.querySelectorAll<HTMLElement>('.js-parallax'));

      // Determine pixel positions. If explicit startPx/endPx are provided
      // use them (allows negative values). Otherwise fall back to the old
      // distancePx behavior which moves from 0 -> distance.
      // const explicit = typeof startPx === 'number' || typeof endPx === 'number';

      items.forEach((el) => {
        // Allow per-element start/end to be specified via `data-start-px` and `data-end-px`.
        // For backward compatibility `data-distance-px` remains supported as the element's endPx.
        const elStartPx = typeof startPx === 'number'
          ? startPx
          : (el.dataset.startPx ? parseFloat(el.dataset.startPx) : 0);

        const elEndPx = typeof endPx === 'number'
          ? endPx
          : (el.dataset.endPx
              ? parseFloat(el.dataset.endPx)
              : (el.dataset.distancePx ? parseFloat(el.dataset.distancePx) : distancePx));

        const positions = { start: elStartPx, end: elEndPx };

        gsap.fromTo(
          el,
          { y: positions.start },
          {
            y: positions.end,
            ease: 'none',
            scrollTrigger: {
              trigger: parallaxContainer,
              start: start ?? 'top bottom',
              end: end ?? 'bottom top',
              scrub: true,
            },
            force3D: true,
          }
        );

        // no explicit trigger collection here; callers can rely on scoped refresh
      });
    }, parallaxContainer);

    return () => {
      try {
        ctx.revert();
      } catch (e) {
        // ignore cleanup errors
      }
    };
  }, [containerRef, distancePx, isEnabled, start, end]);

  // Install auto-refresh for this parallax container so callers don't need to
  // call useScrollTriggerAutoRefresh themselves. This mirrors existing
  // behavior but keeps the API surface smaller for components.
  useScrollTriggerAutoRefresh({ rootRef: containerRef, enabled: isEnabled });
}

export default useParallax;
